import React from 'react';
import { Users, Target, Heart, Zap } from 'lucide-react';
import Footer from '../components/Footer';

const AboutPage = () => {
  const values = [
    {
      icon: <Users size={32} />,
      title: 'Representation',
      description: 'Amplifying Black voices and stories in gaming culture and development.'
    },
    {
      icon: <Target size={32} />,
      title: 'Community',
      description: 'Building connections and support networks for gamers and developers.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Creativity',
      description: 'Celebrating innovative content, gameplay, and game development.'
    },
    {
      icon: <Zap size={32} />,
      title: 'Visibility',
      description: 'Creating platforms and opportunities for underrepresented talent.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-bgn-black">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold mb-8 text-white">
            About BGN
          </h1>
          <p className="text-xl lg:text-2xl text-bgn-grey leading-relaxed max-w-4xl mx-auto">
            Welcome to the Black Gamers Network — a space built to amplify Black voices 
            in gaming and game development.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-bgn-grey/5">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-5xl font-bold mb-8 text-white">
                Our Mission
              </h2>
              <div className="space-y-6 text-lg text-bgn-grey leading-relaxed">
                <p>
                  The Black Gamers Network exists to create visibility, build community, 
                  and provide opportunities for Black gamers, streamers, and developers 
                  in the gaming industry.
                </p>
                <p>
                  We believe that diverse voices make gaming better for everyone. 
                  Our platform connects passionate individuals, shares incredible stories, 
                  and celebrates the contributions of Black creators in gaming culture.
                </p>
                <p>
                  Through streaming partnerships, community events, and educational resources, 
                  we're building the future of inclusive gaming.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_gamers-unite/artifacts/6of96a1d_bgn%20logo.png"
                alt="BGN Logo"
                className="w-80 h-80 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-bgn-black">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-bold mb-16 text-white text-center">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-bgn-grey/10 p-8 rounded-lg hover:bg-bgn-grey/20 transition-colors duration-200"
              >
                <div className="text-bgn-gold mb-4">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-bgn-grey leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Involved Section */}
      <section className="py-24 bg-bgn-grey/5">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-16 text-bgn-black">
            Join Our Community
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-6">
              <div className="bg-bgn-purple rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <Users className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-bold text-bgn-black">For Streamers</h3>
              <p className="text-bgn-black/70">
                Connect with other creators, get featured in our spotlight,
                and grow your audience within our supportive community.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-bgn-gold rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <Target className="text-bgn-black" size={24} />
              </div>
              <h3 className="text-xl font-bold text-bgn-black">For Developers</h3>
              <p className="text-bgn-black/70">
                Share your projects, find collaborators, and get visibility
                for your games and development work.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <Heart className="text-bgn-black" size={24} />
              </div>
              <h3 className="text-xl font-bold text-bgn-black">For Supporters</h3>
              <p className="text-bgn-black/70">
                Discover amazing content, support Black creators,
                and be part of building a more inclusive gaming community.
              </p>
            </div>
          </div>
          
          <div className="mt-12">
            <a 
              href="/account" 
              className="bg-bgn-gold text-bgn-black px-8 py-4 rounded-full font-bold text-lg hover:bg-white transition-colors duration-200 inline-block"
            >
              Get Started Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;